import type { MpEvent, MpGraphFocusSpan } from "./api";
import {
  MAX_TIME_RANGE_DAYS,
  type MpGraphFocus,
  type MpGraphKey,
  serializeFocus,
  serializeGraphs,
  serializeHighlights,
} from "./graphsLink";

/**
 * MP Events display and request helpers. Pure functions — no API, no charts, so
 * this stays cheap to unit test.
 */

/** The window the page opens with. The endpoint rejects 0 and caps at 365. */
export const DEFAULT_EVENT_DAYS_BACK = 30;
export const MIN_EVENT_DAYS_BACK = 1;
export const MAX_EVENT_DAYS_BACK = 365;

/**
 * Display names for the backend's event-type slugs.
 *
 * The taxonomy is table-level: one event per row of each source table, named
 * after the source rather than after a diagnosed fault. Human fault names
 * ("Structural Looseness", "Rolling element bearing") are produced downstream in
 * MySQL and aren't recoverable from the data lake, so they don't appear here.
 *
 * Unmapped slugs still render — `get_mp_event_types` is the source of truth and
 * a new source should show up in the filter without a frontend change.
 */
const EVENT_TYPE_LABELS: Record<string, string> = {
  non_running: "Non-running",
  rca: "RCA",
  rca_cf: "AI Anomaly Alert (RCA-CF)",
  recently_restarted: "Recently Restarted",
  sensor_movement: "Sensor Moved",
  slow_trend: "Slow Trend",
  temp_overload: "Temp Overload",
  vibe_overload: "Vibe Overload",
};

/** The label for an event-type slug, or a readable form of the slug itself. */
export function eventTypeLabel(slug: string): string {
  const mapped = EVENT_TYPE_LABELS[slug];
  if (mapped) {
    return mapped;
  }

  const spaced = slug.replace(/_/g, " ").trim();
  return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : slug;
}

/**
 * Coerce the Days Back input into a value the endpoint accepts.
 *
 * `days_back` is required and validated server-side at 1–365; a 0 (which the
 * page used to read as "all time") is a 400, so it clamps to 1 rather than being
 * sent. The clamped number is written back into the field so the window shown is
 * the window queried.
 */
export function clampDaysBack(value: string | number): number {
  const parsed = typeof value === "number" ? value : Number.parseInt(value, 10);

  if (!Number.isFinite(parsed)) {
    return DEFAULT_EVENT_DAYS_BACK;
  }

  return Math.min(
    MAX_EVENT_DAYS_BACK,
    Math.max(MIN_EVENT_DAYS_BACK, Math.trunc(parsed)),
  );
}

/** Longest `search` the endpoint accepts. */
export const MAX_SEARCH_LENGTH = 64;

/**
 * Reduce a search box value to what `search` accepts: `[A-Za-z0-9_-]`, 1–64
 * characters. Anything else is a 400 from the endpoint, so it's stripped as it's
 * typed rather than rejected on submit — the field only ever holds a monitoring
 * point GUID or a sensor serial, neither of which contains anything else.
 */
export function sanitizeSearch(value: string): string {
  return value.replace(/[^A-Za-z0-9_-]/g, "").slice(0, MAX_SEARCH_LENGTH);
}

const GUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Whether a search term is a whole monitoring point id.
 *
 * A complete GUID is sent as `external_monitoringpoint_id` instead of `search`:
 * `search` is a substring match applied *after* each source's row cap, so on a
 * wide window it filters within the newest rows per source rather than across
 * the whole window. The exact-id filter has no such caveat, and it also sidesteps
 * `_` being a single-character wildcard in the underlying LIKE.
 */
export function isMonitoringPointId(value: string): boolean {
  return GUID_PATTERN.test(value.trim());
}

const SECONDS_PER_DAY = 86_400;

/**
 * Charts opened when an event doesn't say which ones to open — which is most of
 * them, since only `rca_cf` narrows. Keys come from the ML API's graph catalog;
 * `_ts` reads from Timestream, the fresher of the two stores.
 *
 * Ordered as the catalog orders them (RMS → Peak → P2P) so a default link and a
 * narrowed link lay out the same way.
 */
export const EVENT_GRAPHS: MpGraphKey[] = ["rms_ts", "peak_ts", "p2p_ts"];

/**
 * Half-width of the shaded region drawn around an event's timestamp when the
 * event carries no span of its own.
 */
const HIGHLIGHT_PADDING_SECONDS = 12 * 60 * 60;
/** The MP Graphs `days` cap; see `graphsLink.ts` for why it's a copy. */
const MAX_GRAPH_DAYS = MAX_TIME_RANGE_DAYS;
const MIN_GRAPH_DAYS = 30;

/** A span the event carried, or null when it's absent or inverted. */
function focusSpan(span: MpGraphFocusSpan | null | undefined) {
  return span && span.end > span.start ? span : null;
}

/**
 * MP Graphs search params that open the charts an event points at: its graphs,
 * its range, and the span inside that range the model evaluated.
 */
export function graphSearchForEvent(event: MpEvent, now = Date.now()) {
  const nowSeconds = Math.floor(now / 1000);

  // `graph_focus.graphs` names the charts the event is about; a null one still
  // carries a range, so the default set is used with the range applied. Entries
  // whose `highlight` is null or empty open that chart with every series visible.
  const focusEntries = event.graph_focus?.graphs ?? [];
  const charts =
    focusEntries.length > 0
      ? focusEntries.map((entry) => entry.key)
      : EVENT_GRAPHS;

  const focus: MpGraphFocus = new Map(
    focusEntries
      .filter((entry) => entry.highlight && entry.highlight.length > 0)
      .map((entry) => [
        entry.key,
        // Lowercased to match how the chart compares its own series labels.
        (entry.highlight ?? []).map((label) => label.toLowerCase()),
      ]),
  );

  const viewport = focusSpan(event.graph_focus?.viewport);
  const evaluated = focusSpan(event.graph_focus?.highlight_window);

  // The viewport implies the fetch — it runs to the server's now, so for an old
  // event that's far more than the event type's nominal lookback. Derived here
  // rather than carried over from whatever range the user last looked at. Without
  // a viewport, reach back past the event and pad, which is all a bare timestamp
  // supports.
  const days = Math.min(
    MAX_GRAPH_DAYS,
    Math.max(
      MIN_GRAPH_DAYS,
      Math.ceil(
        ((viewport?.end ?? nowSeconds) -
          (viewport?.start ?? event.timestamp - 7 * SECONDS_PER_DAY)) /
          SECONDS_PER_DAY,
      ),
    ),
  );

  // `days` caps at 365, so a viewport reaching further back than the API will
  // return is trimmed to what actually gets fetched — otherwise the chart opens
  // on empty time.
  const fetchStart = nowSeconds - days * SECONDS_PER_DAY;

  // The evaluated span can start before the viewport does (slow_trend looks 62
  // days back inside a 61-day range), so it's clamped rather than the viewport
  // being widened to fit — a band running off-canvas reads as a rendering bug.
  const from = evaluated
    ? Math.max(evaluated.start, viewport?.start ?? fetchStart, fetchStart)
    : event.timestamp - HIGHLIGHT_PADDING_SECONDS;
  const to = evaluated
    ? Math.min(evaluated.end, viewport?.end ?? nowSeconds)
    : event.timestamp + HIGHLIGHT_PADDING_SECONDS;

  return {
    mp: event.external_monitoringpoint_id,
    graph: serializeGraphs(charts),
    days,
    // Distinct from `graphs[].highlight`: this shades a time range and labels it,
    // where that one names series. Orthogonal — when to look, and what at.
    highlight:
      to > from
        ? serializeHighlights(
            charts.map((chart) => ({
              chart,
              from,
              to,
              note: eventTypeLabel(event.event_type),
            })),
          )
        : undefined,
    focus: serializeFocus(focus),
    ...(viewport
      ? { x_start: Math.max(viewport.start, fetchStart), x_stop: viewport.end }
      : {}),
  };
}

/** An event's `metadata` — freeform, shaped by whichever pipeline wrote it. */
export type MpEventMetadata = Record<string, unknown> | unknown[];

function formatMetadataValue(value: unknown): string {
  if (value === null) {
    return "null";
  }
  if (value === undefined) {
    return "";
  }
  // Nested objects/arrays would stringify as "[object Object]".
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

/**
 * Render `metadata` as a single line of `key=value` pairs.
 *
 * Deliberately generic: each source writes its own JSON with no shared schema,
 * so the table shows whatever arrived rather than reaching for known keys.
 */
export function formatEventMetadata(metadata: MpEventMetadata | null): string {
  if (metadata === null) {
    return "—";
  }

  if (Array.isArray(metadata)) {
    return metadata.length === 0
      ? "—"
      : metadata.map(formatMetadataValue).join("; ");
  }

  const entries = Object.entries(metadata);
  return entries.length === 0
    ? "—"
    : entries
        .map(([key, value]) => `${key}=${formatMetadataValue(value)}`)
        .join("; ");
}
